import React, { useState, useEffect } from 'react';

/**
 * AutoScrollImages — auto-rotates through up to 4 images with a soft cross-fade.
 * Used in hotel cards on the homepage listing.
 *
 * Props:
 *   images: array of image URLs (only the first 4 are used)
 *   alt: alt text
 *   interval: ms between transitions (default 2800)
 *   className: extra Tailwind classes on the wrapping <img>
 */
export const AutoScrollImages = ({ images = [], alt = '', interval = 2800, className = '' }) => {
  const pool = (images || []).slice(0, 4);
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (pool.length <= 1 || paused) return undefined;
    const t = setInterval(() => {
      setIdx((i) => (i + 1) % pool.length);
    }, interval);
    return () => clearInterval(t);
  }, [pool.length, paused, interval]);

  if (pool.length === 0) return null;

  return (
    <div
      className="relative w-full h-full overflow-hidden"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      data-testid="auto-scroll-images"
    >
      {pool.map((src, i) => (
        <img
          key={i}
          src={src}
          alt={`${alt} ${i + 1}`}
          loading="lazy"
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ease-in-out ${i === idx ? 'opacity-100' : 'opacity-0'} ${className}`}
        />
      ))}
      {pool.length > 1 && (
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5" data-testid="auto-scroll-dots">
          {pool.map((_, i) => (
            <span
              key={i}
              className={`h-1.5 rounded-full transition-all ${i === idx ? 'w-5 bg-white' : 'w-1.5 bg-white/60'}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};
